import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { Modal } from '../../../../components/modals/Modal'
import { OnClickButton } from '../../../../components/buttons/onclick/OnClickButton'
import { TextInput } from '../../../../components/inputs/Text'
import { Calendar } from '../../../../components/icons/Calendar'
import { VideoCamera, Info, ChatBubbles, User } from '../../../../components/icons'
import { theme } from '../../../../theme/theme'
import type { TimeSlot as TimeSlotType } from '../../../../components/calendar/types'
import { useLocale } from '../../../../hooks/useLocale'
import { BodyLargeMedium, BodySmall } from '../../../../components/styles/Typography'
import { formatDayName } from '../../../../utils/dates'
import i18n from '../../../../i18n'
import { getLanguageNames } from '../../../../utils/timeSlotCalendar'
import { useGetSpecialistById } from '../../../../api/contentful/contentfulQueries'

interface HealthCallConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  selectedSlot: {
    slot: TimeSlotType
    date: Date
  }
  userFullName: string | undefined
  formData: {
    personalNumber: string
    phoneNumber: string
  }
  validationErrors: {
    personalNumber: string
    phoneNumber: string
  }
  onPersonalNumberChange: (value: string) => void
  onPhoneNumberChange: (value: string) => void
  isFormValid: boolean
  isLoading: boolean
}

export const HealthCallConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedSlot,
  userFullName,
  formData,
  validationErrors,
  onPersonalNumberChange,
  onPhoneNumberChange,
  isFormValid,
  isLoading,
}: HealthCallConfirmationModalProps) => {
  const { t } = useTranslation()
  const locale = useLocale()
  const { data: specialistData } = useGetSpecialistById(selectedSlot?.slot.specialistId || '')
  const specialist = specialistData?.items[0]?.fields

  if (!isOpen) return null

  return (
    <Modal onClose={onClose} maxWidth="700px" backgroundColor={theme.color.beigeLight}>
      <ModalContent>
        <Title>{t('employee.healthCall.confirmTitle')}</Title>
        <InfoCard>
          <InfoItem>
            <Calendar size={20} color={theme.color.black} />
            <StyledBodySmall>
              {formatDayName(selectedSlot.date, locale)} {format(selectedSlot.slot.startTime, 'HH:mm')}-
              {format(selectedSlot.slot.endTime, 'HH:mm')}
            </StyledBodySmall>
          </InfoItem>
          <InfoItem>
            <VideoCamera size={20} color={theme.color.black} />
            <BodySmall>{t('employee.healthCall.bookingInfo')}</BodySmall>
          </InfoItem>
          <InfoItem>
            <User size={24} color={theme.color.black} />
            <BodySmall>{`${specialist?.firstName || ''} ${specialist?.lastName || ''}, ${specialist?.jobTitlePortal || specialist?.jobTitle || ''}`}</BodySmall>
          </InfoItem>
          <InfoItem>
            <ChatBubbles size={24} color={theme.color.black} />
            <BodySmall>{specialist?.languages && getLanguageNames(specialist.languages, i18n.language)}</BodySmall>
          </InfoItem>
          <InfoItem>
            <Info size={20} color={theme.color.black} />
            <BodySmall>{t('employee.healthCall.cancellationPolicy')}</BodySmall>
          </InfoItem>
        </InfoCard>
        <FormSection>
          <InfoItem>
            <BodyLargeMedium>{userFullName || ''}</BodyLargeMedium>
          </InfoItem>
          <InputWrapper>
            <TextInput
              label={t('employee.healthCall.personalNumber')}
              name="personnummer"
              value={formData.personalNumber}
              onChange={(e) => onPersonalNumberChange(e.target.value)}
              type="text"
              placeholder={t('employee.healthCall.personalNumberPlaceholder')}
              inputMode="numeric"
            />
            {validationErrors.personalNumber && <ErrorText>{validationErrors.personalNumber}</ErrorText>}
          </InputWrapper>
          <InputWrapper>
            <TextInput
              label={t('employee.healthCall.phoneNumber')}
              name="telefonnummer"
              value={formData.phoneNumber}
              onChange={(e) => onPhoneNumberChange(e.target.value)}
              onFocus={(e) => {
                if (!e.target.value || e.target.value === '') {
                  onPhoneNumberChange('+46')
                }
              }}
              type="tel"
              placeholder="+4670XXXXXXX"
              inputMode="numeric"
            />
            {validationErrors.phoneNumber && <ErrorText>{validationErrors.phoneNumber}</ErrorText>}
          </InputWrapper>
        </FormSection>
        <OnClickButton
          text={t('employee.healthCall.confirmBooking')}
          onClick={onConfirm}
          disabled={!isFormValid || isLoading}
          loading={isLoading}
        />
      </ModalContent>
    </Modal>
  )
}

const ModalContent = styled.div`
  padding: ${theme.spacing.xlarge}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.large}px;
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding: 0;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: ${theme.fontSize.large}px;
  color: ${theme.color.black};
  text-align: center;
  font-weight: 400;
`

const InfoCard = styled.div`
  background-color: ${theme.color.white};
  border-radius: ${theme.spacing.small}px;
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
`

const InfoItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${theme.spacing.small}px;
  align-items: start;

  svg {
    margin-top: 2px;
  }
`

const StyledBodySmall = styled(BodySmall)`
  text-transform: capitalize;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ErrorText = styled.span`
  color: ${theme.color.red};
  font-size: ${theme.fontSize.xsmall}px;
  margin-top: ${theme.spacing.tiny}px;
`
